import { call, put, select } from 'redux-saga/effects';

import {
  updateLoadingUser,
  updateMatch,
  updateUserAndPatient,
} from 'connect/actions';
import { getUserSignupErrorType } from 'connect/constants';
import { extractErrorCode, extractErrorMessage, sendToLogger } from 'connect/saga/helpers';
import { getUserAndPatient } from 'connect/saga/saga';
import { selectDataMatch, selectDataMatchToken, selectDataPatient, selectDataUserAuthenticated } from 'connect/selectors';

import { CheckoutStateHandler } from 'pages/checkout/connect/state-handlers';
import Logger from 'utils/Logger';
import { POST, request } from 'utils/request';

import { UpdatePatientDetails } from '../patient/update-details';
import { SagaHandler } from '../SagaHandler';
import { UpsertIterableUser } from '../upsert-iterable-user/upsert-iterable-user';

import { PRETTY_SIGNUP_ERROR_MESSAGES as USER_SIGNUP_ERROR_MESSAGES } from './errorMessageConstants';

const apiAuthenticateUser = (userData) => request(
  POST,
  `${CONFIG.API_URL}/auth/triage`,
  {
    ...userData,
    signupType: 'patient',
  },
);

/* This file is copied from signup.js and modified to use the new auth/triage endpoint */
// Eventually it would be nice to get rid of signup.js and just use this file
class _Authenticate extends SagaHandler {
  constructor() {
    super({
      actionName: 'data/AUTHENTICATE_USER',
      stateKey: 'authenticate',
    });
  }

  createAction(userData, match, matchToken) {
    return {
      type: this._actionName,
      userData,
      match,
      matchToken,
    };
  }

  getUserErrorMessage(errorType) {
    if (!errorType) {
      return null;
    }

    const userErrorMessage = USER_SIGNUP_ERROR_MESSAGES[errorType];

    return userErrorMessage ?? 'An unknown error occurred';
  }

  * #handleAuthentication(userData) {
    const valueHandler = this.value;

    // If user is already authenticated, we don't need to sign them up again
    const isUserAuthenticated = yield select(selectDataUserAuthenticated);
    if (isUserAuthenticated) {
      return;
    }

    const response = yield call(apiAuthenticateUser, userData);

    const { user: updatedUser, isNewUser } = response;

    if (updatedUser) {
      yield put(valueHandler.update({ user: updatedUser, isNewUser }));
      const populatedUser = yield call(getUserAndPatient);
      yield put(updateUserAndPatient(populatedUser));
      return;
    }

    const { phoneToSave, phoneToUpdate, otpSent } = response;

    yield put(valueHandler.update({
      phoneToSave,
      phoneToUpdate,
      otpSent,
      isNewUser,
    }));

    const checkoutState = yield select(CheckoutStateHandler.selector);

    // Save phoneToSave and phoneToUpdate inside checkoutState, so they are not cleared on refresh
    yield put(CheckoutStateHandler.update({
      ...checkoutState,
      account: {
        ...checkoutState.account,
        phoneToSave,
        phoneToUpdate,
      },
    }));
  }

  * saga({ userData, match, matchToken }) {
    const errorHandler = this.error;
    const loadingHandler = this.loading;

    if (!userData?.mobilePhone || !userData?.email) {
      Logger.error('mobilePhone and email are required', { userData });
      yield put(errorHandler.update('Phone and email are required'));
      return;
    }

    const currentMatch = match || (yield select(selectDataMatch));
    const currentMatchToken = matchToken || (yield select(selectDataMatchToken));

    yield put(updateLoadingUser(true));
    yield put(loadingHandler.update(true));
    yield put(errorHandler.update(null));

    const patientData = {};
    if (currentMatch) {
      patientData.metadata = userData?.metadata || {};
      patientData.metadata.wizardData = { ...currentMatch.other };
      patientData.metadata.wizardData.location = currentMatch.location;
    }

    let user;
    try {
      user = yield call(getUserAndPatient);

      // Authentication handler
      // Must be a generator function, therefore must use the function* keyword
      yield this.#handleAuthentication(userData);

      // Add ownership of match to authenticated user
      if (currentMatch?.id && currentMatchToken) {
        yield call(updateMatch, {
          matchId: currentMatch.id,
          currentMatchToken,
        });
      }

      const patient = yield select(selectDataPatient);

      const isLoggedIn = Boolean(user?.id && patient?.id);
      const hasPatientMetadata = Object.keys(patientData.metadata || {}).length;

      // If anything in patientData.metadata, make additional API call
      if (isLoggedIn && hasPatientMetadata) {
        yield put(UpdatePatientDetails.createAction({ patientDetails: patientData, autoVerify: true }));
      }
    } catch (error) {
      const errorMessage = extractErrorMessage(error);
      const errorCode = extractErrorCode(error);
      const errorType = getUserSignupErrorType(errorMessage, errorCode);
      yield put(errorHandler.update(errorType));

      sendToLogger(error, 'AuthenticateUser');
    } finally {
      yield put(updateLoadingUser(false));
      yield put(loadingHandler.update(false));
    }

    // Separate try/catch block for Iterable user creation, after everything else is done.
    // This should never interfere with user signup in any way
    try {
      if (!user?.email) {
        return;
      }

      const { other } = currentMatch || {};

      const dataFields = {
        signedUpInCheckout: Boolean(currentMatch),
        questionReason: other?.reason,
        questionSubReasons: other?.subReasons?.join(', '),
        selectedTreatments: other?.selectedTreatments
          ?.map((treatment) => treatment.title)
          ?.join(', '),
      };

      yield call(UpsertIterableUser.saga, {
        payload: {
          dataFields,
        },
      });
    } catch (error) {
      sendToLogger(error, 'upsertIterableUser');
    }
  }
}

export const Authenticate = new _Authenticate();
